import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Sparkles, Crown, MessageCircle, ArrowRight, Instagram, TrendingUp, Wallet, Users, Heart, Rocket, Gem, Droplets, Scissors, HeartPulse, Star, Layers, Package } from 'lucide-react';
import Seo from '../components/Seo';
import useReveal from '../hooks/useReveal';
import { useLang } from '../context/LanguageContext';
import { IMAGES } from '../mock/mock';
import { WHATSAPP_URL, track } from '../components/landingUtils';
import CompetitionSpotlight from '../components/CompetitionSpotlight';
import { getLandingCopy } from '../i18n/landing';
import CaseStudies from '../components/CaseStudies';

const WHY_ICONS = [Crown, TrendingUp, Wallet, Users, Rocket, Heart];
const IDEA_ICONS = [Droplets, Sparkles, Scissors, HeartPulse, Star, Gem, Package, Layers];

const Kicker = ({ children, light }) => (
  <span className={`inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase ${light ? 'text-gold-light' : 'text-gold'}`}>
    <span className="w-8 h-px bg-gold" />{children}<span className="w-8 h-px bg-gold" />
  </span>
);

export default function InfluencerBrand() {
  const { lang } = useLang();
  const L = getLandingCopy(lang);
  const c = L.ib;
  useReveal(lang);
  useEffect(() => { track('landing_page_view', { page: 'influencer-beauty-brand' }); }, []);

  return (
    <div>
      <Seo title="Launch Your Own Beauty Brand"
        description="Turn your audience into your own beauty brand. ARGE Cosmetics helps influencers and creators develop premium skincare, haircare and beauty products from concept to launch."
        path="/influencer-beauty-brand"
        image="/og-competition.jpg"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'Service', name: 'Influencer Beauty Brand Development', provider: { '@type': 'Organization', name: 'ARGE Cosmetics', url: 'https://argecosmetics.co.uk' }, serviceType: 'Beauty brand development for influencers and creators' }} />

      <section className="relative min-h-[92vh] flex items-center pt-32 pb-20 overflow-hidden bg-espresso">
        <img src="/images/competition_hero.jpg" alt="Beauty influencer holding her own branded luxury cosmetic product" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-espresso via-espresso/80 to-espresso/25" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold text-espresso text-[0.68rem] tracking-[0.3em] uppercase font-medium mb-6"><Instagram className="w-3.5 h-3.5" /> {c.badge}</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.08]">{c.h1a} <span className="text-gold-light italic">{c.h1b}</span></h1>
            <p className="mt-6 text-lg text-ivory/85 leading-relaxed max-w-xl">{c.sub}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" onClick={() => track('start_brand_click', { page: 'influencer-hero' })} data-testid="ib-hero-primary-cta" className="gold-sheen inline-flex items-center gap-3 px-10 py-4 bg-gold text-espresso text-xs tracking-[0.2em] uppercase font-medium hover:bg-gold-light transition-colors">{c.cta1} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>
              <Link to="/competition" onClick={() => track('competition_click', { page: 'influencer-hero' })} data-testid="ib-hero-competition-cta" className="inline-flex items-center gap-3 px-10 py-4 border border-gold-light/60 text-gold-light text-xs tracking-[0.2em] uppercase hover:bg-gold-light hover:text-espresso transition-colors"><Trophy className="w-4 h-4" /> {c.cta2}</Link>
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" onClick={() => track('whatsapp_click', { page: 'influencer' })} data-testid="ib-whatsapp-link" className="mt-6 inline-flex items-center gap-2 text-sm text-ivory/70 hover:text-gold-light transition-colors"><MessageCircle className="w-4 h-4 text-gold-light" /> {c.wa}</a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker>{c.whyK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4">{c.whyT}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.why.map((wp, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <div key={wp.t} className="reveal bg-cream p-8 border-t-2 border-gold hover:-translate-y-1 transition-transform duration-300">
                  <Icon className="w-7 h-7 text-gold mb-5" />
                  <h3 className="font-serif text-2xl text-espresso leading-snug">{wp.t}</h3>
                  <p className="mt-3 text-sm text-mocha leading-relaxed">{wp.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-espresso text-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker light>{c.procK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl mt-4">{c.procT}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {c.steps.map((s, i) => (
              <div key={s.t} className="reveal border-l border-gold/40 pl-6">
                <p className="font-serif text-5xl text-gold/50">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-serif text-2xl mt-3 text-gold-light uppercase tracking-wide">{s.t}</h3>
                <p className="mt-2 text-sm text-ivory/75 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12 reveal">
            <Kicker>{c.launchK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4">{c.launchT}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {c.ideas.map((idea, i) => {
              const Icon = IDEA_ICONS[i];
              return (
                <div key={idea} className="reveal bg-ivory p-7 text-center border border-gold-soft hover:border-gold transition-colors">
                  <Icon className="w-7 h-7 text-gold mx-auto mb-3" />
                  <h3 className="font-serif text-lg text-espresso">{idea}</h3>
                </div>
              );
            })}
          </div>
          <p className="mt-10 text-center text-sm text-mocha reveal">{c.crossPre}<Link to="/private-label-cosmetics" className="text-gold underline underline-offset-4 hover:text-espresso transition-colors">{c.crossL1}</Link>{c.crossPost}</p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ivory">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal order-2 lg:order-1">
            <img src={IMAGES.serumSplash} alt="Bespoke luxury serum developed for a creator brand" loading="lazy" className="w-full h-[420px] object-cover" />
          </div>
          <div className="reveal order-1 lg:order-2">
            <Kicker>{c.audK}</Kicker>
            <h2 className="font-serif text-3xl md:text-4xl text-espresso mt-4">{c.audT}</h2>
            <p className="mt-4 text-mocha">{c.audP}</p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {c.audience.map((a) => (
                <li key={a} className="flex items-center gap-2 text-sm text-espresso"><span className="w-1.5 h-1.5 bg-gold rounded-full shrink-0" />{a}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CaseStudies />

      <section className="reveal">
        <div className="text-center pt-16 pb-2 bg-ivory">
          <Kicker>{c.compK}</Kicker>
          <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4 mb-8">{c.compT}</h2>
          <p className="text-mocha max-w-xl mx-auto mb-10 px-6">{c.compP}</p>
        </div>
        <div onClickCapture={() => track('competition_click', { page: 'influencer-spotlight' })}>
          <CompetitionSpotlight />
        </div>
      </section>

      <section className="py-20 md:py-28 bg-cream text-center">
        <div className="max-w-3xl mx-auto px-6 reveal">
          <Kicker>{c.finalK}</Kicker>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl text-espresso">{c.finalT1} <span className="text-gold italic">{c.finalT2}</span></h2>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/contact" onClick={() => track('start_brand_click', { page: 'influencer-footer' })} data-testid="ib-final-cta" className="gold-sheen inline-flex items-center gap-3 px-12 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors">{c.cta3} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" onClick={() => track('whatsapp_click', { page: 'influencer-footer' })} className="inline-flex items-center gap-2 px-10 py-4 border border-espresso/30 text-espresso text-xs tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors"><MessageCircle className="w-4 h-4" /> {c.cta4}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
